<script setup lang="ts">
import { ref, computed, watchEffect } from 'vue'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { useAccounts } from '@/composables/useAccounts'
import { useAuth } from '@/composables/useAuth'
import { formatMoney } from '@/lib/utils/money'
import PensionPlanIdAlert from '@/components/alerts/PensionPlanIdAlert.vue'
import { addDays, isBefore } from 'date-fns'

const { user } = useAuth()
const closedAt = ref(localStorage.getItem('roundupNotificationClosedAt'))
const { accounts, applyRoundups, clearRoundups } = useAccounts()
const isLoading = ref(false)
const showPensionAlert = ref(false)

const isClosed = computed(() => {
  if (!closedAt.value) return false
  const closedDate = new Date(closedAt.value)
  const nextShowDate = addDays(closedDate, 1)
  return isBefore(new Date(), nextShowDate)
})

const showRoundupNotification = computed(
  () =>
    !showPensionAlert.value &&
    !isClosed.value &&
    pendingSum.value > 2000 &&
    pendingTransactions.value.length > 0,
)

const pendingTransactions = computed(() =>
  accounts.value
    .filter((account) => account.roundupsEnabled)
    .flatMap((account) => account.transactions)
    .filter((transaction) => transaction.roundupApplied === null),
)

const pendingSum = computed(() =>
  pendingTransactions.value.reduce(
    (acc, transaction) => acc + transaction.roundup,
    0,
  ),
)

watchEffect(() => {
  console.log({
    closedAt: closedAt.value,
    isClosed: isClosed.value,
    pendingSum: pendingSum.value,
    pendingTransactions: pendingTransactions.value.length,
    accounts: accounts.value,
  })
})

async function handleConfirm() {
  if (!user.value?.pension_plan_id) {
    showPensionAlert.value = true
    return
  }

  try {
    isLoading.value = true
    await applyRoundups()
    closedAt.value = new Date().toISOString()
    localStorage.setItem('roundupNotificationClosedAt', closedAt.value)
  } catch (error) {
    console.error('Failed to apply roundups:', error)
  } finally {
    isLoading.value = false
  }
}

async function handleDecline() {
  try {
    isLoading.value = true
    await clearRoundups()
    closedAt.value = new Date().toISOString()
    localStorage.setItem('roundupNotificationClosedAt', closedAt.value)
  } catch (error) {
    console.error('Failed to clear roundups:', error)
  } finally {
    isLoading.value = false
  }
}
</script>

<template>
  <Transition
    enter-active-class="transition duration-300 ease-out"
    enter-from-class="transform -translate-y-2 opacity-0"
    enter-to-class="transform translate-y-0 opacity-100"
    leave-active-class="transition duration-200 ease-in"
    leave-from-class="transform translate-y-0 opacity-100"
    leave-to-class="transform -translate-y-2 opacity-0"
  >
    <div class="fixed top-24 left-1/2 -translate-x-1/2 max-w-md space-y-4">
      <!-- Pension Plan ID Missing Alert -->
      <PensionPlanIdAlert v-if="showPensionAlert" return-to="/roundups" />

      <!-- Regular Roundup Notification -->
      <Alert
        v-if="showRoundupNotification"
        class="shadow-lg border-primary/20 bg-white"
        variant="default"
      >
        <AlertTitle>Daily Retirement Savings</AlertTitle>
        <AlertDescription class="mt-2">
          Confirm your daily investment towards your retirement with the rounded
          up amount of {{ formatMoney(pendingSum / 100) }}
        </AlertDescription>

        <div class="mt-4 flex gap-3 justify-end">
          <Button
            variant="outline"
            :disabled="isLoading"
            @click="handleDecline"
          >
            Decline
          </Button>
          <Button :disabled="isLoading" @click="handleConfirm">
            Confirm
          </Button>
        </div>
      </Alert>
    </div>
  </Transition>
</template>
